import React, { useEffect, useState, useCallback, memo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import GameHeader from '../components/GameHeader';
import PlayersList from '../components/PlayersList/PlayersList';
import DictionaryBoard from '../components/Dictionary/DictionaryBoard';
import WordCreation from '../components/WordCreation/WordCreation';
import AdvancedWordCreation from '../components/WordCreation/AdvancedWordCreation';
import TurnSetup from '../components/TurnManagement/TurnSetup';
import WordDescription from '../components/TurnManagement/WordDescription';
import Guessing from '../components/TurnManagement/Guessing';
import EraComplete from '../components/TurnManagement/EraComplete';
import GameCompleted from '../components/TurnManagement/GameCompleted';
import { checkServerStatus, checkServer, assumeServerRunning } from '../utils/serverCheck';
import './ModularGame.css';

// Memoize components that don't need to re-render often
const MemoizedPlayersList = memo(PlayersList);
const MemoizedDictionaryBoard = memo(DictionaryBoard);

// Loading component
const LoadingScreen = ({ onRetry, onSkipCheck, error }) => (
  <div className="loading-screen">
    <div className="loading-content">
      <h2>Connecting to server...</h2>
      <div className="loading-spinner"></div>
      <p>If this takes too long, the server might be down or unreachable.</p>
      <div className="retry-buttons">
        <button className="retry-button" onClick={onRetry}>
          Retry Connection
        </button>
        <button className="skip-check-button" onClick={onSkipCheck}>
          Skip Check
        </button>
      </div>
    </div>
  </div>
);

const ModularGame = () => {
  const { gameId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [playerName, setPlayerName] = useState(location.state?.playerName || '');
  const [showLoading, setShowLoading] = useState(true);
  const [serverError, setServerError] = useState('');
  const { initializeSocket, isConnected, gameState, currentEra, error, setError } = useGame();

  // Check if we're on a mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  useEffect(() => {
    if (!playerName) {
      navigate('/');
      return;
    }

    // Skip server check and directly connect to the Cloudflare tunnel
    console.log('Skipping server check and directly connecting to Cloudflare tunnel');
    assumeServerRunning();
    initializeSocket(playerName, gameId);
    
    // Hide loading screen after a short delay
    setTimeout(() => {
      setShowLoading(false);
    }, 2000);

    // Clean up function
    return () => {
      // Any cleanup code here
    };
  }, [playerName, gameId, navigate, initializeSocket]);

  const handleRetry = async () => {
    setServerError('');
    try {
      await checkServerStatus();
      initializeSocket(playerName, gameId);
      setTimeout(() => {
        setShowLoading(false);
      }, 3000);
    } catch (error) {
      setServerError(error.message);
    }
  };

  const handleSkipCheck = () => {
    setServerError('');
    assumeServerRunning();
    initializeSocket(playerName, gameId);
    setTimeout(() => {
      setShowLoading(false);
    }, 3000);
  };

  if (showLoading) {
    return (
      <LoadingScreen 
        onRetry={handleRetry} 
        onSkipCheck={handleSkipCheck}
        error={serverError} 
      />
    );
  }

  // Render game content based on game state
  const renderGameContent = () => {
    switch (gameState) {
      case 'waiting':
        return (
          <div className="waiting-section">
            <h3>Waiting for players to join...</h3>
            <p>Share the Game ID with your friends to invite them!</p>
          </div>
        );
      case 'word_creation':
        return currentEra === 0 ? <WordCreation /> : <AdvancedWordCreation />;
      case 'turn_setup':
        return <TurnSetup />;
      case 'describing':
        return <WordDescription />;
      case 'guessing':
        return <Guessing />;
      case 'era_complete':
        return <EraComplete />;
      case 'completed':
        return <GameCompleted />;
      default:
        return null;
    }
  };

  return (
    <div className="modular-game-container">
      <GameHeader />
      
      <div className="game-layout">
        {/* Left sidebar - Players */}
        <div className="game-sidebar players-sidebar">
          <MemoizedPlayersList />
        </div>
        
        {/* Center - Game content */}
        <div className="game-main">
          {renderGameContent()}
        </div>
        
        {/* Right sidebar - Dictionary */}
        <div className="game-sidebar dictionary-sidebar">
          <MemoizedDictionaryBoard />
        </div>
      </div>

      {/* Error message display */}
      {(error || serverError) && (
        <div className="error-message">
          {error || serverError}
          {(error || serverError) && (
            <div className="error-actions">
              <button onClick={handleRetry} className="error-retry-btn">Retry</button>
              <button onClick={handleSkipCheck} className="error-skip-btn">Skip Check</button>
            </div>
          )}
        </div>
      )}
      
      {/* Connection status indicator */}
      <div className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
        <div className="connection-status-dot"></div>
        {isConnected ? 'Connected' : 'Disconnected'}
      </div>
    </div>
  );
};

export default ModularGame; 